import { useEffect, useMemo, useState, useRef } from "react";
import "./Header.css";

import { fetchClientData } from "../../services/Client.api";
import GlobalLoading from "../../components/GlobalLoading/GlobalLoading";
import Message from "../../components/Message/Message";
import ExportOptionsModal from "../../components/ExportOptionsModal/ExportOptionsModal";
import { exportDashboardPDF } from "../../utils/exportDashboardPDF";
import { exportDashboardPPTX } from "../../utils/exportDashboardPPTX";
import { useDashboardFilters } from "../../context/DashboardFiltersContext";

import { ReactComponent as SunIcon } from "../../assets/icons/sun.svg";
import { ReactComponent as MoonIcon } from "../../assets/icons/moon.svg";
import { ReactComponent as ExitIcon } from "../../assets/icons/logout.svg";

export default function Header({ exportDisabled = false }) {
  const exportAbortRef = useRef(null);
  const { appliedFilters } = useDashboardFilters();

  const [theme, setTheme] = useState(() => localStorage.getItem("theme") || "light");
  const [companyName, setCompanyName] = useState("");
  const [loadingClient, setLoadingClient] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportStepText, setExportStepText] = useState("Preparando o PDF…");
  const [clientBrand, setClientBrand] = useState("");
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [availableCharts, setAvailableCharts] = useState([]);

  const [message, setMessage] = useState({ type: "", message: "" });

  const clientId = localStorage.getItem("clientId");

  useEffect(() => {
    document.body.classList.toggle("dark-theme", theme === "dark");
    localStorage.setItem("theme", theme);
  }, [theme]);

  useEffect(() => {
    if (!message?.message) return;

    const timer = setTimeout(() => {
      setMessage({ type: "", message: "" });
    }, 5000);

    return () => clearTimeout(timer);
  }, [message]);

  function showBlockedExportMessage() {
    setMessage({
      type: "error",
      message: "Aguarde o carregamento completo dos dados para exportar o relatório.",
    });
    setTimeout(() => {
      setMessage({ type: "", message: "" });
    }, 5000);
  }

  function toggleTheme() {
    setTheme((prev) => {
      const next = prev === "dark" ? "light" : "dark";
      localStorage.setItem("theme", next);
      document.body.classList.toggle("dark-theme", next === "dark");
      window.dispatchEvent(new Event("theme-change"));
      return next;
    });
  }

  useEffect(() => {
    let mounted = true;

    async function loadClient() {
      if (!clientId) {
        setLoadingClient(false);
        return;
      }

      try {
        const data = await fetchClientData(clientId);
        if (!mounted) return;

        setCompanyName(data?.company_name || "");

        const brandRaw = data?.brand || "";
        const dashBase =
          (process.env.REACT_APP_DASHBOARD_URL || "").replace(/\/+$/, "") + "/";

        setClientBrand(
          brandRaw
            ? brandRaw.replace(process.env.REACT_APP_S3_URL || "", dashBase)
            : ""
        );
      } catch (err) {
        console.error("Erro ao carregar dados do cliente:", err);
        if (mounted) setCompanyName("");
      } finally {
        if (mounted) setLoadingClient(false);
      }
    }

    loadClient();

    return () => {
      mounted = false;
    };
  }, [clientId]);

  const todayFormatted = useMemo(() => {
    return new Date().toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  }, []);

  function formatDateBR(dateStr) {
    if (!dateStr) return "—";
    const [y, m, d] = dateStr.split("-");
    return `${d}/${m}/${y}`;
  }

  function formatPeriod(startDate, endDate) {
    if (!startDate || !endDate) return "Período não informado";
    return `${formatDateBR(startDate)} — ${formatDateBR(endDate)}`;
  }

  function getAvailableExportCharts() {
    const root = document.querySelector("#pdf-dashboard");
    if (!root) return [];

    const seen = new Set();

    return Array.from(root.querySelectorAll("[data-export-title]"))
      .map((card) => String(card.getAttribute("data-export-title") || "").trim())
      .filter(Boolean)
      .filter((title) => {
        if (seen.has(title)) return false;
        seen.add(title);
        return true;
      })
      .map((title, index) => ({
        id: `${index}-${title}`,
        title,
      }));
  }

  function handleOpenExportModal() {
    if (exportDisabled) {
      showBlockedExportMessage();
      return;
    }

    if (exporting) return;

    const charts = getAvailableExportCharts();
    if (!charts.length) {
      setMessage({
        type: "error",
        message: "Nenhum gráfico disponível para exportar.",
      });
      return;
    }

    setAvailableCharts(charts);
    setExportModalOpen(true);
  }

  async function handleExport({ format, selectedTitles }) {
    if (exportDisabled) {
      showBlockedExportMessage();
      return;
    }

    if (exporting) return;

    const controller = new AbortController();
    exportAbortRef.current = controller;

    setExportModalOpen(false);
    setExporting(true);
    document.body.classList.add("is-exporting");
    window.dispatchEvent(new Event("export-mode-change"));
    setExportProgress(0);
    setExportStepText("Iniciando…");

    try {
      const dark = localStorage.getItem("theme") === "dark";
      const backgroundColor = dark ? "#0b1220" : "#ffffff";

      const clientName = companyName || "Cliente";
      const periodText = formatPeriod(
        appliedFilters?.startDate,
        appliedFilters?.endDate
      );

      const common = {
        filename: "relatorio-dashboard",
        backgroundColor,
        scale: 2,
        clientName,
        clientBrand,
        periodText,
        titles: null,
        selectedTitles,
        signal: controller.signal,
        onProgress: ({ progress, step }) => {
          if (typeof progress === "number") setExportProgress(progress);
          if (step) setExportStepText(step);
        },
      };

      if (format === "pdf") await exportDashboardPDF(common);
      if (format === "pptx") await exportDashboardPPTX(common);
    } catch (err) {
      if (controller.signal.aborted) {
        console.log("Export cancelado.");
        return;
      }
      console.error("Erro export:", err);
      setMessage({
        type: "error",
        message: "Não foi possível exportar o relatório.",
      });
    } finally {
      exportAbortRef.current = null;

      document.body.classList.remove("is-exporting", "exporting-pdf");
      window.dispatchEvent(new Event("export-mode-change"));

      setExporting(false);
      setExportProgress(0);
      setExportStepText("Preparando…");

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.dispatchEvent(new CustomEvent("dashboard-export:end"));
        });
      });
    }
  }

  function handleLogout() {
    const token = localStorage.getItem("token");
    const clientId = localStorage.getItem("clientId");

    localStorage.removeItem("token");
    localStorage.removeItem("clientId");

    if (token && clientId) {
      window.location.href = `${process.env.REACT_APP_CLIENT_URL}${clientId}/telaNoticias?token=${token}`;
    } else {
      window.location.href = process.env.REACT_APP_CLIENT_URL;
    }
  }

  function handleCancelExport() {
    if (exportAbortRef.current) {
      exportAbortRef.current.abort();
    }
    setExporting(false);
    setExportProgress(0);
    setExportStepText("Cancelado.");
  }

  const title = loadingClient ? "Carregando..." : companyName || "Cliente";
  const isDark = theme === "dark";
  const isExportBlocked = exportDisabled || exporting;

  return (
    <header className="dashHeader">
      <div className="dashHeader__glow" aria-hidden="true" />
      <div className="dashHeader__noise" aria-hidden="true" />

      <div className="dashHeader__content">
        <div className="dashHeader__left">
          <div className="dashHeader__kicker">
            <span className="dashHeader__kickerDot" aria-hidden="true" />
            <span className="dashHeader__kickerText">Relatório de mídia</span>
          </div>

          <h1 className="dashHeader__title" title={title}>
            {title}
          </h1>

          <p className="dashHeader__subtitle">
            Análise de repercussão em mídia e monitoramento de notícias
          </p>

          <div className="dashHeader__meta">
            <span className="dashMetaChip">
              <span className="dashMetaChip__dot" aria-hidden="true" />
              <span className="dashMetaChip__text">{todayFormatted}</span>
            </span>
          </div>
        </div>

        <div className="dashHeader__right">
          {message?.message ? (
            <div className="dashHeader__message">
              <Message type={message.type} message={message.message} />
            </div>
          ) : null}

          <div className="dashHeader__actions">
            <div className="dashTooltip">
              <button
                type="button"
                onClick={toggleTheme}
                className="dashAction dashAction--icon"
                aria-label={isDark ? "Ativar tema claro" : "Ativar tema escuro"}
              >
                <span className="dashAction__icon">
                  {isDark ? <SunIcon /> : <MoonIcon />}
                </span>
              </button>

              <span className="dashTooltip__content">
                {isDark ? "Tema claro" : "Tema escuro"}
              </span>
            </div>

            <div className="dashActionSep" aria-hidden="true" />

            <button
              type="button"
              onClick={handleOpenExportModal}
              className={`dashAction dashAction--primary ${
                isExportBlocked ? "dashAction--locked" : ""
              }`}
              aria-disabled={isExportBlocked}
              title={
                exportDisabled
                  ? "Aguarde o carregamento dos dados para exportar"
                  : "Exportar relatório"
              }
            >
              <span className="dashAction__label">Exportar</span>
            </button>

            <button
              type="button"
              onClick={handleLogout}
              className="dashAction dashAction--danger"
            >
              <span className="dashAction__icon">
                <ExitIcon />
              </span>
              <span className="dashAction__label">Sair</span>
            </button>
          </div>
        </div>
      </div>

      <GlobalLoading
        visible={exporting}
        title="Gerando relatório"
        subtitle={exportStepText}
        progress={exportProgress}
        onCancel={handleCancelExport}
      />

      <ExportOptionsModal
        open={exportModalOpen}
        charts={availableCharts}
        loading={exporting}
        onClose={() => setExportModalOpen(false)}
        onExport={handleExport}
      />
    </header>
  );
}
